import { authLogout } from 'actions/AuthActions'
import { pushWithRegion } from 'actions/NavigationActions'
import useAgentHubBookingOnboardingNeedHelp from 'agentHub/onboarding/useAgentHubBookingOnboardingNeedHelp'
import AvatarCircle from 'components/Common/AvatarCircle/AvatarCircle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useToggle from 'hooks/useToggle'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Caption from 'LuxKit/Typography/Caption'
import { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import AgentHubAccountMenuModal from './AgentHubAccountMenuModal'

function AgentHubAccountMenu() {
  const intl = useIntl()
  const [menuOpen, toggleMenuOpen, _, closeMenu] = useToggle(false)
  const { canReplay, label: needHelpLabel, start: startOnboarding } = useAgentHubBookingOnboardingNeedHelp()
  const onNeedHelpClick = useCallback(() => {
    startOnboarding()
    closeMenu()
  }, [closeMenu, startOnboarding])

  const dropdownTrigger = useRef<HTMLDivElement>(null)
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const lastName = useAppSelector(state => state.auth.account.surname)
  const dispatch = useAppDispatch()

  const isMobile = useScreenSizeOnly('mobile')

  const showModal = useContext(ModalContext)
  const onLogout = useCallback(() => {
    dispatch(authLogout())
    dispatch(pushWithRegion('/'))
  }, [dispatch])

  const onMenuClick = useCallback(() => {
    if (isMobile) {
      showModal(<AgentHubAccountMenuModal />)
    } else {
      toggleMenuOpen()
    }
  }, [isMobile, showModal, toggleMenuOpen])

  return <>
    <div ref={dropdownTrigger} data-agent-hub-onboarding="account">
      <CSSBreakpoint min="desktop">
        <TextButton
          kind="tertiary"
          variant="default"
          aria-label={intl.formatMessage({
            id: 'gorJlYuG-iz5dBWrK-bw',
            defaultMessage: 'Account',
          })}
          onClick={onMenuClick}
          startIcon={<AvatarCircle
            size="small"
            firstName={firstName}
            lastName={lastName}
            nonInteractable
          />}
        >
          <Caption variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'QK9DnlFk5MzJzC6R8lGi',
              defaultMessage: 'Account',
            })}
          </Caption>
        </TextButton>
      </CSSBreakpoint>
      <CSSBreakpoint max="tablet">
        <AvatarCircle
          size="small"
          firstName={firstName}
          lastName={lastName}
          onClick={onMenuClick}
        />
      </CSSBreakpoint>
    </div>
    <DropdownList
      size="S"
      open={menuOpen}
      triggerRef={dropdownTrigger}
      anchorRef={dropdownTrigger}
      onClose={closeMenu}
      placement="bottom-end"
    >
      <ClickableListItem
        title={intl.formatMessage({
          id: '1F5_SFM0AvByo4kJQ5iL',
          defaultMessage: 'View dashboard',
        })}
        href={config.AGENT_PORTAL_URL}
        target="_blank"
      />
      {canReplay && (
        <ClickableListItem
          title={needHelpLabel}
          onClick={onNeedHelpClick}
        />
      )}
      <ClickableListItem
        title={intl.formatMessage({
          id: 'vt47BM8mzbA_XPoJV7z8',
          defaultMessage: 'Log out',
        })}
        onClick={onLogout}
      />
    </DropdownList>
  </>
}

export default AgentHubAccountMenu
